import { AsyncPipe, NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Optional, Output } from '@angular/core';
import { EMPTY, Observable, of } from 'rxjs';
import { FireflyLocalizationService } from '../utils/localization/firefly-localization.service';
import { pluralize } from '../utils/localization/pluralize';
import { COLUMNS_MIN_SELECTED_COUNT, COLUMNS_TOTAL_COUNT } from './layout-width.constants';
import { LayoutWidthColumn, LayoutWidthConfiguration } from './layout-width.model';

@Component({
  selector: 'f-layout-width',
  templateUrl: './layout-width.component.html',
  standalone: true,
  imports: [AsyncPipe, NgClass],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FireflyLayoutWidthComponent implements OnInit {
  _columnsTotalCount: number = COLUMNS_TOTAL_COUNT.MAX;

  @Input() set configuration(config: LayoutWidthConfiguration) {
    if (
      config?.totalColumnsToDisplay !== undefined &&
      config.totalColumnsToDisplay < COLUMNS_TOTAL_COUNT.MAX &&
      config.totalColumnsToDisplay > COLUMNS_TOTAL_COUNT.MIN
    ) {
      this._columnsTotalCount = config.totalColumnsToDisplay;
    } else {
      this._columnsTotalCount = COLUMNS_TOTAL_COUNT.MAX;
    }

    if (config.minAllowedColumns < COLUMNS_MIN_SELECTED_COUNT) {
      this._minAllowedColumns = COLUMNS_MIN_SELECTED_COUNT;
      console.warn(
        `minAllowedColumns can't be less then ${COLUMNS_MIN_SELECTED_COUNT}. Value changed to ${COLUMNS_MIN_SELECTED_COUNT}`
      );
    } else this._minAllowedColumns = config.minAllowedColumns;
    if (config.maxAllowedColumns > this._columnsTotalCount) {
      this._maxAllowedColumns = this._columnsTotalCount;
      console.warn(
        `maxAllowedColumns can't be greater then ${this._columnsTotalCount}. Value changed to ${this._columnsTotalCount}`
      );
    } else this._maxAllowedColumns = config.maxAllowedColumns;
    if (config.selectedColumnsCount < this._minAllowedColumns) {
      this._selectedColumnsCount = this._minAllowedColumns;
      console.warn(
        `selectedColumnsCount can't be less then minAllowedColumns. Value changed to ${this._minAllowedColumns}`
      );
    } else if (config.selectedColumnsCount > this._maxAllowedColumns) {
      this._selectedColumnsCount = this._maxAllowedColumns;
      console.warn(
        `selectedColumnsCount can't be greater then maxAllowedColumns. Value changed to ${this._maxAllowedColumns}`
      );
    } else this._selectedColumnsCount = config.selectedColumnsCount;

    this.setMinDisabledTitle();
    this.setMaxDisabledColumnTitle();
    this.setColumns();
  }

  @Input() disabled = false;

  @Output() selectedColumnsCountChange = new EventEmitter<number>();

  constructor(@Optional() private localizationService: FireflyLocalizationService) {}

  private _minAllowedColumns = COLUMNS_MIN_SELECTED_COUNT;
  private _maxAllowedColumns = COLUMNS_TOTAL_COUNT.MAX;
  private _selectedColumnsCount = COLUMNS_MIN_SELECTED_COUNT;
  columns: LayoutWidthColumn[] = [];
  minDisabledColumnTitle$: Observable<string> = EMPTY;
  maxDisabledColumnTitle$: Observable<string> = EMPTY;

  ngOnInit(): void {
    this.setMinDisabledTitle();
    this.setMaxDisabledColumnTitle();
    this.setColumns();
  }

  onSelectColumnsCount(columnIndex: number) {
    if (columnIndex >= this._maxAllowedColumns) return;
    if (columnIndex + 1 < this._minAllowedColumns) return;

    this.columns = this.columns.map((column, index) =>
      index <= columnIndex || index < this._minAllowedColumns
        ? { ...column, selected: true }
        : { ...column, selected: false }
    );

    this.selectedColumnsCountChange.emit(columnIndex + 1);
  }

  get minColumns() {
    return this._minAllowedColumns;
  }

  get maxColumns() {
    return this._maxAllowedColumns;
  }

  private setColumns() {
    this.columns = Array(this._columnsTotalCount)
      .fill({})
      .map(
        (_, index) =>
          ({
            selected: index < this._selectedColumnsCount,
            disabled: index < this._minAllowedColumns - 1 || index >= this._maxAllowedColumns
          } as LayoutWidthColumn)
      );
  }

  private setMaxDisabledColumnTitle() {
    if (this.localizationService) {
      this.maxDisabledColumnTitle$ = this.localizationService.localize(
        pluralize('maxDisabledColumn', this._maxAllowedColumns),
        { columnCount: this._maxAllowedColumns }
      );
    } else {
      this.maxDisabledColumnTitle$ = of(
        pluralize(`This section cannot be more than ${this._maxAllowedColumns} column`, this._maxAllowedColumns)
      );
    }
  }

  private setMinDisabledTitle() {
    if (this.localizationService) {
      this.minDisabledColumnTitle$ = this.localizationService.localize(
        pluralize('minDisabledColumn', this._minAllowedColumns),
        { columnCount: this._minAllowedColumns }
      );
    } else {
      this.minDisabledColumnTitle$ = of(
        pluralize(`This section cannot be less than ${this._minAllowedColumns} column`, this._minAllowedColumns)
      );
    }
  }
}
